'use client';

import { c as _c } from "react/compiler-runtime";
import { useMemo, useState } from 'react';
import { usePostHog } from 'posthog-js/react';
import { cx } from 'class-variance-authority';
import { Checkbox, ComboBox, Icon, Toggle, type IconName } from '~/components/primitives';
import { SUGGESTED_RADIUS_KM, type ActivityLocationFilterType } from '@funkey/constants';
import { GoogleMapsAutocomplete } from '~/features/google/maps';
import { NEAR_POINT_CLEARED, NEAR_POINT_PICKED, isNearPointActive, useHasPrefilledNearPoint } from '~/features/activities/search/client/nearPoint';
import { type ActivityFilterConfig } from './shared';

/** Radius presets (km) suggested in the distance input (custom values allowed). */
export const RADIUS_OPTIONS = [5, 10, 25, 50, 100] as const;
export const DEFAULT_RADIUS_KM = SUGGESTED_RADIUS_KM.city;

/** Route options map onto the existing `location` filter type. */
const ROUTE_OPTIONS: ReadonlyArray<{
  value: ActivityLocationFilterType;
  icon: IconName;
  label: (i18n: Parameters<ActivityFilterConfig<any>['label']>[0]) => string;
}> = [{
  value: 'FIXED',
  icon: 'car-retro',
  label: i18n => i18n._('activity.filter.location.option.fixed', undefined, {
    message: 'I go to the partner'
  })
}, {
  value: 'CUSTOM',
  icon: 'house-pin',
  label: i18n => i18n._('activity.filter.location.option.custom', undefined, {
    message: 'Location of choice'
  })
}];

/**
 * Location filter (replaces the old region picker): a city/address input that
 * resolves to coordinates (Belgium + the Netherlands only), a radius, and the
 * two route options. Distance filtering + sorting run at the DB layer.
 */
type LocationFilterState = {
  nearLat: number | null;
  nearLng: number | null;
  nearLabel: string | null;
  radius: number | null;
  nearCleared: boolean | null;
  location: ActivityLocationFilterType[];
  sort: 'popularity.DESC' | 'price.ASC' | 'price.DESC' | 'createdAt.ASC' | 'createdAt.DESC' | 'distance.ASC' | null;
};
type LocationFieldProps = {
  i18n: Parameters<NonNullable<ActivityFilterConfig<LocationFilterState>['PopupContent']>>[0]['i18n'];
  value: LocationFilterState;
  onChange: React.Dispatch<React.SetStateAction<LocationFilterState>>;
};

/** City autocomplete + radius combobox on one row. Shared by the desktop
 * popup (gray card seams) and the mobile filter bar (blue hairlines). */
const CityDistanceRow: React.FC<LocationFieldProps & {
  source: 'dropdown' | 'mobile-bar';
  dividerClassName: string;
}> = props => {
  const $ = _c(42);
  const {
    value
  } = props;
  const posthog = usePostHog();
  const nearLabel = value.nearCleared ? null : value.nearLabel;
  const radius = value.nearCleared ? null : value.radius;
  const [radiusSearch, setRadiusSearch] = useState("");
  let t0;
  if ($[0] !== radius || $[1] !== radiusSearch) {
    const kms = new Set(RADIUS_OPTIONS);
    if (radius != null) {
      kms.add(radius);
    }
    let t1;
    if ($[3] !== radiusSearch) {
      t1 = radiusSearch.trim();
      $[3] = radiusSearch;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    const typed = Number(t1);
    if (Number.isFinite(typed) && typed > 0) {
      kms.add(typed);
    }
    t0 = [...kms].sort(_temp).map(_temp2);
    $[0] = radius;
    $[1] = radiusSearch;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const radiusOptions = t0;
  const t1 = nearLabel ?? "";
  let t2;
  if ($[5] !== props.i18n) {
    t2 = props.i18n._("Enter a city or address");
    $[5] = props.i18n;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== props.i18n) {
    t3 = props.i18n._("google-maps.search.placeholder");
    $[7] = props.i18n;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== props) {
    t4 = label => props.onChange(prev => ({
      ...prev,
      nearLabel: label || null
    }));
    $[9] = props;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== nearLabel || $[12] !== posthog || $[13] !== props || $[14] !== value.location) {
    t5 = coords => {
      posthog.capture("location.search", {
        near_label: nearLabel ?? null,
        latitude: Number(coords.latitude),
        longitude: Number(coords.longitude),
        radius_km: coords.suggestedRadiusKm,
        routes: value.location,
        source: props.source
      });
      props.onChange(prev_0 => ({
        ...prev_0,
        ...NEAR_POINT_PICKED({
          latitude: coords.latitude,
          longitude: coords.longitude,
          radiusKm: coords.suggestedRadiusKm
        })
      }));
    };
    $[11] = nearLabel;
    $[12] = posthog;
    $[13] = props;
    $[14] = value.location;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== t1 || $[17] !== t2 || $[18] !== t3 || $[19] !== t4 || $[20] !== t5) {
    t6 = <div className="min-w-0 flex-1"><GoogleMapsAutocomplete value={t1} placeholder={t2} searchPlaceHolder={t3} className="rounded-none border-0 shadow-none" onChange={t4} onResolve={t5} /></div>;
    $[16] = t1;
    $[17] = t2;
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== props.dividerClassName) {
    t7 = <div className={props.dividerClassName} />;
    $[22] = props.dividerClassName;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  const t8 = radius != null ? `${radius} km` : "";
  let t9;
  if ($[24] !== props.i18n) {
    t9 = props.i18n._("Any distance");
    $[24] = props.i18n;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== props.i18n) {
    t10 = props.i18n._("e.g. 30");
    $[26] = props.i18n;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  let t12;
  if ($[28] !== props) {
    t11 = val => {
      setRadiusSearch(val);
      const typed_0 = Number.parseInt(val, 10);
      if (Number.isFinite(typed_0) && typed_0 > 0) {
        props.onChange(prev_1 => ({
          ...prev_1,
          radius: typed_0
        }));
      }
    };
    t12 = selected => props.onChange(prev_2 => ({
      ...prev_2,
      radius: selected ? Number.parseInt(selected, 10) : null
    }));
    $[28] = props;
    $[29] = t11;
    $[30] = t12;
  } else {
    t11 = $[29];
    t12 = $[30];
  }
  let t13;
  if ($[31] !== radiusOptions || $[32] !== t10 || $[33] !== t11 || $[34] !== t12 || $[35] !== t8 || $[36] !== t9) {
    t13 = <ComboBox value={t8} options={radiusOptions} placeholder={t9} searchPlaceHolder={t10} onSearch={t11} onChange={t12} className="w-32 shrink-0 rounded-none border-0 shadow-none" />;
    $[31] = radiusOptions;
    $[32] = t10;
    $[33] = t11;
    $[34] = t12;
    $[35] = t8;
    $[36] = t9;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  let t14;
  if ($[38] !== t13 || $[39] !== t6 || $[40] !== t7) {
    t14 = <div className="flex items-stretch">{t6}{t7}{t13}</div>;
    $[38] = t13;
    $[39] = t6;
    $[40] = t7;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  return t14;
};

/** Route toggle pair; seam styling comes from the caller. */
const RouteToggles: React.FC<LocationFieldProps & {
  className: string;
  toggleClassName?: (idx: number) => string;
  iconClassName?: string;
  /** Render a checkbox on the left and push the icon to the far right. */
  withCheckbox?: boolean;
}> = props => {
  const $ = _c(5);
  const t0 = props.className;
  let t1;
  if ($[0] !== props) {
    t1 = ROUTE_OPTIONS.map((option, idx) => {
      const pressed = props.value.location.includes(option.value);
      const icon = <Icon name={option.icon} strokeWidth={1.5} className={cx("h-4 w-4 shrink-0", props.iconClassName)} />;
      return <Toggle key={option.value} className={cx("group rounded-none border-0 bg-white leading-tight whitespace-normal hover:bg-white data-[state=on]:hover:bg-secondary-50", props.toggleClassName?.(idx))} pressed={pressed} onPressedChange={() => props.onChange(prev => ({
        ...prev,
        location: prev.location.includes(option.value) ? prev.location.filter(route => route !== option.value) : [...prev.location, option.value]
      }))}>{props.withCheckbox ? <><Checkbox checked={pressed} readOnly={true} tabIndex={-1} className="pointer-events-none shrink-0" /><span className="min-w-0 flex-1 text-left">{option.label(props.i18n)}</span>{icon}</> : <>{icon}{option.label(props.i18n)}</>}</Toggle>;
    });
    $[0] = props;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== props.className || $[3] !== t1) {
    t2 = <div className={t0}>{t1}</div>;
    $[2] = props.className;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
export const LOCATION_RADIUS_FILTER_CONFIG: ActivityFilterConfig<LocationFilterState> = {
  key: 'regions',
  // Wider popover than the other filters: it holds the city input, radius combobox
  // and the two route toggles side by side. Sized to content so the row never
  // overflows the popover; capped so it doesn't run off-screen.
  popoverClassName: 'w-auto min-w-[560px] max-w-[760px]',
  label: i18n => i18n._('Location'),
  placeholder: i18n => i18n._('Anywhere'),
  selectionDisplay(i18n, value) {
    if (isNearPointActive(value)) {
      const place = value.nearLabel || i18n._('Selected location');
      return value.radius ? `${place} · ${value.radius} km` : place;
    }
    if (value.location?.length === 1) {
      return ROUTE_OPTIONS.find(option => option.value === value.location[0])?.label(i18n);
    }
    if ((value.location?.length ?? 0) > 1) return i18n._('Both routes');
    return undefined;
  },
  PopupContent(props) {
    const {
      value
    } = props;
    const hasNearPoint = isNearPointActive(value);
    const hasSelection = hasNearPoint || (value.location?.length ?? 0) > 0;
    const hasPrefilledNearPoint = useHasPrefilledNearPoint();
    return <div className="relative flex w-full flex-col lg:w-[34rem] lg:max-w-[85vw]">
                {hasSelection && <button type="button" className="absolute -top-9 right-0 text-sm text-blue-500 hover:underline" onClick={() => props.onChange(prev => ({
        ...prev,
        ...NEAR_POINT_CLEARED({
          hasPrefilledNearPoint,
          extra: {
            location: [],
            sort: null
          }
        })
      }))}>
                        {props.i18n._('Clear')}
                    </button>}
                {/* One joined card: location + distance on top, route toggles flush below. */}
                <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
                    <CityDistanceRow i18n={props.i18n} value={value} onChange={props.onChange} source="dropdown" dividerClassName="my-2 w-px bg-gray-200" />

                    {/* Route toggles — flush against the inputs above; joined seams stay
                        joined when they stack on narrow screens (gap-px grid). */}
                    <RouteToggles i18n={props.i18n} value={value} onChange={props.onChange} withCheckbox className="grid grid-cols-2 gap-px border-t border-gray-200 bg-gray-200 max-[420px]:grid-cols-1" toggleClassName={() => 'h-12 justify-start gap-3 px-4 text-left'} iconClassName="text-primary group-data-[state=on]:text-secondary" />
                </div>
            </div>;
  },
  mobile: {
    icon: 'location',
    ctaText: i18n => i18n._('Choose location'),
    /* Flat full-bleed rows matching the budget/participants rows below:
     * blue-100 hairline seams instead of the rounded gray card. The
     * negative margins cancel the mobile row wrapper's px-4 py-2. */
    Component(props) {
      return <div className="-mx-4 -my-2 flex min-w-0 flex-1 flex-col">
                    <CityDistanceRow i18n={props.i18n} value={props.value} onChange={props.onChange} source="mobile-bar" dividerClassName="w-px bg-blue-100" />
                    <RouteToggles i18n={props.i18n} value={props.value} onChange={props.onChange} withCheckbox className="grid grid-cols-1 gap-px border-t border-blue-100 bg-blue-100 min-[480px]:grid-cols-2"
        // Checkbox + label aligned left, route icon pushed to the right.
        // gap-px seams divide the cells in both 1- and 2-column layouts.
        toggleClassName={() => 'h-14 justify-start gap-3 px-4 text-left'}
        // Same icon color as the rows below; green only when selected.
        iconClassName="text-primary group-data-[state=on]:text-secondary" />
                </div>;
    }
  }
};
function _temp(a, b) {
  return a - b;
}
function _temp2(km) {
  return {
    value: String(km),
    label: `${km} km`
  };
}